import { Box, Button, Stack, Typography } from '@mui/material'
import { Page } from 'components/Page'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { PageHeader } from 'components/Shared/PageHeader'
import { useMemo, useState } from 'react'
import { LEConnectUserList } from './LEConnectUserList'
import { useGetLicensedEstablishmentUsersByCorporateAccount } from 'hooks/api/useGetLicensedEstablishmentUsersByCorporateAccount'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { type LicensedEstablishmentUser } from 'src/types/api'
import { useNavigate } from 'react-router-dom'
import { getLEPermissions } from 'src/navigation/pathPermissions'
import { useSetMe } from 'stores/useMeQuery'
import { CheckboxInput } from 'components/CheckBox/CheckBox'

export const LicensedEstablishmentUsersPage = () => {
  const navigate = useNavigate()
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const [checked, setChecked] = useState(false)
  const currentCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const {
    isPending,
    isError,
    data: leUsers,
  } = useGetLicensedEstablishmentUsersByCorporateAccount(
    currentCorporateAccountId
  )

  // Show users as long as they are staff or owner for at least one location
  const usersByType = leUsers?.filter(
    (user) =>
      user.licensedEstablishments?.[0]?.type === 'Owner' ||
      user.licensedEstablishments?.[0]?.type === 'Staff'
  )

  // Filter users with no access
  // No access means: corporate accounts that match the selected corporate account but has access to no locations under the CA
  const noAccessUsers = leUsers?.filter(
    (user) =>
      user.licensedEstablishmentUserCorporateAccounts?.some(
        (x) =>
          (x.corporateAccountId === currentCorporateAccountId &&
            user.licensedEstablishments === null) ||
          (x.corporateAccountId === currentCorporateAccountId &&
            user.licensedEstablishments?.length === 0)
      )
  )

  const handleCheckboxChange = () => {
    setFilteredData(undefined) // Reset filtered data when checkbox is toggled
    setChecked(!checked)
  }

  const [filteredData, setFilteredData] =
    useState<LicensedEstablishmentUser[]>()

  useMemo(() => {
    setFilteredData(undefined)
  }, [currentCorporateAccountId])

  // Get the LE permissions
  const lePermissions = getLEPermissions(
    meQuery?.data?.licensedEstablishments,
    meQuery?.data?.user?.licensedEstablishments,
    currentCorporateAccountId
  )

  return (
    <Page
      header={
        <Box>
          <PageHeader
            title="J&J Connect Users"
            isSecondary={true}
            headerBgColor="white"
          />
        </Box>
      }
    >
      {(meQuery.isPending || isPending) && <ActivityIndicator />}
      {(meQuery.isError || isError) && <ErrorIndicator />}
      {!lePermissions ? (
        <NoDataIndicator
          noImage
          noDataMessage="Your current permissions do not allow access to view J&J Connect Users. To obtain access, please contact your Account Manager for assistance."
        />
      ) : (
        <>
          <Stack
            direction={{ md: 'row', sm: 'column', xs: 'column' }}
            gap={{ md: 0, sm: 2, xs: 2 }}
            justifyContent={'space-between'}
          >
            <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
              <GridSearchField<'FullLicensedEstablishmentUserDTO'>
                data={(checked ? noAccessUsers : usersByType) ?? []}
                propsToSearch={['firstName', 'lastName', 'email', 'type']}
                placeholder="Search J&J Connect Users"
                handleFilteredData={setFilteredData}
                fullWidth
                focused
              />
            </Box>

            <Button
              variant="contained"
              onClick={async () => {
                await navigate(
                  '/LicensedEstablishmentUsers/NewLEConnectUserPage'
                )
              }}
            >
              <Typography>Create New J&J Connect User</Typography>
            </Button>
          </Stack>
          <CheckboxInput
            checked={checked}
            text="Show Users with No Access Only"
            onClick={handleCheckboxChange}
          />

          <LEConnectUserList
            users={
              filteredData ?? (checked ? noAccessUsers : usersByType) ?? []
            }
          />
        </>
      )}
    </Page>
  )
}
